/**
 * Philippine formats. Money arrives from the API as decimal strings (Decimal(18,2))
 * and is formatted with decimal.js, never through a float. Dates are stored UTC and
 * shown in Asia/Manila (UTC+8, no DST).
 */
import Decimal from 'decimal.js';

export { Decimal };

export function dec(value: string | number | null | undefined): Decimal {
  if (value === null || value === undefined || value === '') return new Decimal(0);
  try {
    return new Decimal(value);
  } catch {
    return new Decimal(0);
  }
}

const group = (integer: string): string => integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** 1234567.891 → "1,234,567.89" (half-up to 2 places). */
export function amount(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const d = dec(value).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  const [integer, fraction] = d.abs().toFixed(2).split('.');
  return `${d.isNegative() && !d.isZero() ? '-' : ''}${group(integer)}.${fraction}`;
}

/** "₱1,234,567.89", "-₱25.00"; whole=true drops ".00" for tiles ("₱1,250"). */
export function peso(value: string | number | null | undefined, whole = false): string {
  if (value === null || value === undefined || value === '') return '—';
  let text = amount(value);
  if (whole && text.endsWith('.00')) text = text.slice(0, -3);
  return text.startsWith('-') ? `-₱${text.slice(1)}` : `₱${text}`;
}

/** Compact for KPI tiles: ₱12.4M, ₱830K, ₱950. */
export function pesoCompact(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const d = dec(value);
  const abs = d.abs();
  const sign = d.isNegative() ? '-' : '';
  if (abs.gte(1e9)) return `${sign}₱${abs.div(1e9).toDecimalPlaces(1).toString()}B`;
  if (abs.gte(1e6)) return `${sign}₱${abs.div(1e6).toDecimalPlaces(1).toString()}M`;
  if (abs.gte(1e4)) return `${sign}₱${abs.div(1e3).toDecimalPlaces(0).toString()}K`;
  return peso(value, true);
}

/** Normalizes typed input ("1,250.5", "₱ 900") into "1250.50", or null when invalid. */
export function parseAmount(input: string): string | null {
  const clean = input.replace(/[,\s₱]/g, '');
  if (clean === '') return null;
  if (!/^-?\d+(\.\d{0,2})?$/.test(clean)) return null;
  return new Decimal(clean).toFixed(2);
}

export const count = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : group(String(n)));

export function percent(value: number | string | null | undefined, digits = 1): string {
  if (value === null || value === undefined || value === '') return '—';
  return `${dec(value).toDecimalPlaces(digits).toFixed(digits)}%`;
}

// ---- Dates (Asia/Manila) ----
const MANILA = 8 * 3_600_000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const m = (d: string | Date): Date => new Date((typeof d === 'string' ? new Date(d) : d).getTime() + MANILA);
const valid = (v: string | Date): boolean => !Number.isNaN((typeof v === 'string' ? new Date(v) : v).getTime());

/** "6 Oct 2026" */
export function shortDate(v: string | Date | null | undefined): string {
  if (!v || !valid(v)) return '—';
  const d = m(v);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "3:05 PM" */
export function time(v: string | Date | null | undefined): string {
  if (!v || !valid(v)) return '—';
  const d = m(v);
  const h = d.getUTCHours();
  return `${h % 12 || 12}:${String(d.getUTCMinutes()).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

/** "3:05:09 PM" — for scan reads where seconds matter. */
export function timeSec(v: string | Date | number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  const d = m(new Date(v));
  const h = d.getUTCHours();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${h % 12 || 12}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())} ${h < 12 ? 'AM' : 'PM'}`;
}

export const dateTime = (v: string | Date | null | undefined): string => (v && valid(v) ? `${shortDate(v)}, ${time(v)}` : '—');

/** "Today, 3:00 PM" / "Yesterday, 9:00 AM" / "Sat 12 Oct, 9:00 AM" */
export function when(v: string | Date | null | undefined, now: Date = new Date()): string {
  if (!v || !valid(v)) return '—';
  const d = m(v);
  const today = m(now);
  const day = (x: Date) => Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate());
  const diff = Math.round((day(d) - day(today)) / 86_400_000);
  const label = diff === 0 ? 'Today' : diff === -1 ? 'Yesterday' : diff === 1 ? 'Tomorrow' : `${DAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
  return `${label}, ${time(v)}`;
}

/** "just now", "5 min ago", "2 h ago", "3 days ago" */
export function ago(v: string | Date | null | undefined, now: number = Date.now()): string {
  if (!v || !valid(v)) return '—';
  const s = Math.round((now - new Date(v).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  const days = Math.floor(s / 86400);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** Manila calendar date as YYYY-MM-DD (for date inputs and report periods). */
export const manilaDay = (d: Date = new Date()): string => m(d).toISOString().slice(0, 10);
